import {
    Component,
    Event,
    EventEmitter,
    h,
    Host,
    Listen,
    Prop,
    State,
} from '@stencil/core';
import {
    ServiceType,
    ThemeType,
} from '@uipath/portal-shell-util';

import { Scope } from '../../constants/constants';
import { SERVICE_IDS } from '../../constants/defaultServiceOrder';
import { HelpMenuEvent } from '../../models/appInsights/events/help-menu.events';
import { PortalHelpEvents } from '../../models/appInsights/events/portalHelp.events';
import {
    createLinkFromActiveService,
    HelpMenuAction,
} from '../../models/helpActions';
import store from '../../store/store';
import { accountAndTenantsStore } from '../../utils/accountAndTenants/accountAndTenants';
import { AccountAndTenantsInstanceResolver } from '../../utils/accountAndTenants/accountAndTenantsInstanceResolver';
import { isMsiOrStandalone } from '../../utils/EnvironmentUtil';
import {
    getScopedUrlFromDevProps,
    parseOrReturn,
} from '../../utils/helper';
import { t } from '../../utils/localization/loc';
import Logger from '../../utils/logger/logger';
import { sessionStore } from '../../utils/session/sessionStore';
import { getTheme } from '../../utils/themeHelper';
import { buildHelpMenu } from './helpMenuBuilder';

@Component({
    tag: 'portal-help',
    styleUrl: 'portal-help.scss',
    shadow: true,
})
export class PortalHelp {
    /**
     * Triggered when logout was clicked on the portal menu
     * @event PortalHelp
     */
    @Event({
        eventName: 'helpItemClicked',
        composed: false,
        cancelable: true,
        bubbles: true,
    }) helpItemClicked: EventEmitter<{ id: string }>;

    /**
     * Array of additional help-menu items
     */
    @Prop() helpItems: HelpMenuAction[] | string = [];

    /**
     * Service id to reference a contextual help entry (ie, 'orchestrator')
     */
    @Prop() serviceId: string = SERVICE_IDS.home;

    /**
     * Theme override to bypass store state
     * @memberof PortalHelp
     */
    @Prop() theme: ThemeType = 'autoTheme';

    /**
     * Language
     * @memberof PortalHelp
     */
    @Prop() language: string = 'en';

    /**
     * isMenuOpen tracks where or not the help menu is currently shown
     */
    @State() isMenuOpen: boolean = false;

    @Listen('portalMenuSurfaceOpened')
    onPortalMenuSurfaceOpened(_event: CustomEvent) {
        Logger.logCustomEvent(HelpMenuEvent.HelpMenuToggled, { opened: true });
        this.isMenuOpen = true;
    }

    @Listen('portalMenuSurfaceClosed')
    onPortalMenuSurfaceClosed(_event: CustomEvent) {
        Logger.logCustomEvent(HelpMenuEvent.HelpMenuToggled, { opened: false });
        this.isMenuOpen = false;
    }

    render() {
        const helpLabel = t('help', this.language);
        const menuItems = parseOrReturn<HelpMenuAction[]>(this.helpItems, /* fallback */ []);
        const contextualDocumentationLink = createLinkFromActiveService(
            this.serviceId,
            accountAndTenantsStore.get('menuItems'),
            this.language,
        );

        // Only show the contextual documentation link if there aren't user-provided actions.
        // If the service is providing help items, they can do a better job providing a contextual 'documentation' link
        const allItems = menuItems.length > 0 ? [ ...menuItems ] : [ contextualDocumentationLink ];
        if (!this.isDownloadsMenuItemHidden()) {
            allItems.push(this.getDownloadsMenuItem(this.language));
        }

        const builtMenu = buildHelpMenu(allItems, this.language);

        return (
            <Host class={getTheme(store, this.theme)}>
                <portal-menu
                    data-test="portal-help"
                    menuList={builtMenu}
                    onActionClicked={async event => this.helpItemClicked.emit({ id: event.detail.actionId })}
                    theme={this.theme}>
                    <portal-icon-button
                        haspopup={true}
                        expanded={this.isMenuOpen}
                        showVisualFocus={!this.isMenuOpen}
                        name="help_outline"
                        ariaLabel={helpLabel}
                        theme={this.theme}
                    ></portal-icon-button>
                </portal-menu>
            </Host>
        );
    }

    private isDownloadsMenuItemHidden = () => {
        const msiOrStandalone = isMsiOrStandalone();
        const isK8sHost = sessionStore.get('isHost') && !msiOrStandalone;
        return msiOrStandalone
            || isK8sHost
            || (!AccountAndTenantsInstanceResolver.Instance?.isAdminUserGroup()
                    && AccountAndTenantsInstanceResolver.Instance?.isResourceCenterHiddenForNonAdmins());
    };

    private getDownloadsMenuItem = (locale: string): HelpMenuAction => {
        const downloadUrl =
                getScopedUrlFromDevProps(
                    ServiceType.cloudRpa,
                    Scope.Global,
                ) + '/resourcecenter';

        return {
            category: 'resources',
            text: t('help-menu-downloads', locale),
            type: 'link',
            href: downloadUrl,
            customEvent: PortalHelpEvents.ClickDownloads,
        };
    };
}
